import { FC, useMemo, useState } from 'react';
import { ColumnsType } from 'antd/es/table';
import { Empty, Table, List as AntdList } from 'antd';
import { useTranslate } from '@refinedev/core';
import { IconStatus } from '../../../../icon-status';
import TablePagination from '../../../../ui/table-pagination';
import { useLocalPagination } from '../../../../../hooks/useLocalPagination';
import tableStyles from '../../tableStyles.module.scss';
import type { TableProps } from 'antd';

interface ValidationProps {
    data: any[];
    group: string;
    [key: string]: any;
}

interface Column {
    category: string;
    code: number;
    comment: string | null;
    criteria: string;
    details: string;
    status: string;
    onFilter: () => void;
}

type Filter = {
    value: string | boolean | number;
    record: any;
};

export const Validation: FC<ValidationProps> = ({ data, group, ...props }) => {
    const t = useTranslate();
    const [statusFilter, setStatusFilter] = useState<string | null>(null);

    const onChangeRowClassName = ({ status }: { status: string }) => {
        return status === 'validation_error' ? 'verror' : '';
    };

    const columns: ColumnsType<Column> = [
        {
            title: t(`${group}.status`),
            dataIndex: 'status',
            render: (value, row) => value && <IconStatus type={value} />,
            filteredValue: statusFilter ? [statusFilter] : null,
            filters: [
                {
                    text: t(`${group}.validation_successful`),
                    value: 'validation_successful',
                },
                {
                    text: t(`${group}.awaiting_validation`),
                    value: 'awaiting_validation',
                },
                {
                    text: t(`${group}.validation_error`),
                    value: 'validation_error',
                },
                {
                    text: t(`${group}.validation_warning`),
                    value: 'validation_warning',
                },
                {
                    text: t(`${group}.validation_notice`),
                    value: 'validation_notice',
                },
            ],
            onFilter: (value, record) =>
                typeof record.status === 'string' &&
                record.status.indexOf(String(value)) === 0,
            filterMultiple: false,
        },
        {
            title: t(`${group}.code`),
            dataIndex: 'code',
        },
        {
            title: t(`${group}.category`),
            dataIndex: 'category',
        },
        {
            title: t(`${group}.comment`),
            dataIndex: 'comment',
        },
        {
            title: t(`${group}.criteria`),
            dataIndex: 'criteria',
        },
        {
            title: t(`${group}.details`),
            dataIndex: 'details',
            render: value => {
                return !value || value?.length === 0 ? (
                    <div></div>
                ) : (
                    <AntdList
                        header={null}
                        footer={null}
                        dataSource={value ?? []}
                        renderItem={(item: any) => (
                            <AntdList.Item key={item}>{item}</AntdList.Item>
                        )}
                        size="small"
                    />
                );
            },
            width: 300,
        },
    ];

    const normalizedData = useMemo(
        () => (Array.isArray(data) ? data : []),
        [data]
    );

    const mappedData: Column[] = useMemo(
        () =>
            normalizedData.map(item => ({
                ...(item as Column),
            })),
        [normalizedData]
    );

    const filteredData = useMemo(() => {
        if (!statusFilter) return mappedData;

        return mappedData.filter(record => {
            return (
                typeof record.status === 'string' &&
                record.status.indexOf(statusFilter) === 0
            );
        });
    }, [mappedData, statusFilter]);

    const {
        currentPage,
        pageSize,
        pageSizeOptions,
        onChangePage,
        onChangePageSize,
        startIndex,
        endIndex,
    } = useLocalPagination(filteredData.length);

    const paginatedData = useMemo(
        () => filteredData.slice(startIndex, endIndex),
        [filteredData, startIndex, endIndex]
    );

    const hasData = filteredData.length > 0;

    const handleTableChange: TableProps<Column>['onChange'] = (_, filters) => {
        const nextStatus =
            (filters?.status?.[0] as string | undefined | null) ?? null;

        setStatusFilter(nextStatus);
        onChangePage(1);
    };

    if (!hasData) {
        return <Empty description={t('applications.titles.no_data')} />;
    }

    return (
        <div className={tableStyles.tableWrapper}>
            <Table
                columns={columns}
                dataSource={paginatedData}
                rowClassName={onChangeRowClassName}
                rowKey="code"
                pagination={false}
                className={tableStyles.table}
                {...props}
                onChange={handleTableChange}
            />
            <TablePagination
                currentPage={currentPage}
                pageSize={pageSize}
                totalItems={filteredData.length}
                pageSizeOptions={pageSizeOptions}
                onChangePage={onChangePage}
                onChangePageSize={onChangePageSize}
                previousLabel={t(
                    'applications.pagination.previous',
                    'Попередня сторінка'
                )}
                nextLabel={t(
                    'applications.pagination.next',
                    'Наступна сторінка'
                )}
                showDocumentsLabel={t(
                    'applications.pagination.show_documents',
                    'Показувати документи'
                )}
                rangeLabel={(start, end, total) =>
                    `${start}-${end} ${t(
                        'applications.pagination.of_total',
                        'з'
                    )} ${total}`
                }
            />
        </div>
    );
};
